import React from 'react'
import {
  Divider,
  Box,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  useTheme,
} from '@mui/material'
import { styled } from '@mui/system'
import type { Theme } from '@mui/material'
import LabelValue from '../Components/LabelValue'
import BannerBox from '../Components/BannerBox'
import InfoSection from '../Components/InfoSection'
import Header, { HeaderVariant } from '../../Elements/Header'
import { useTranslation } from 'react-i18next'
import { Event } from '../../../swagger'
import { dateToDashString, IS_UTC } from '../../../utils/dateUtility'

export const INTERNAL_EVENT_LOCATION_TEST_IDS = {
  locationStartDateValue: 'internal-event-location-start-date-value',
  locationEndDateValue: 'internal-event-location-end-date-value',
  registrationEarlyCloseDateValue:
    'internal-event-location-early-close-date-value',
} as const

const EventSection = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  '& .MuiTextField-root': {
    margin: theme.spacing(1),
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(2),
  },
}))

const defaultHeaderContainerProps = (theme: Theme) => ({
  ml: 1.5,
  mt: 2,
  mb: 2,
  [theme.breakpoints.down('sm')]: {
    mt: 1,
    mb: 0.5,
  },
})

const EventDetailsForm = styled('div')(({ theme }) => ({
  display: 'block',
  [theme.breakpoints.down('sm')]: {
    display: 'block',
  },
}))

const FieldRow = styled(Box)(({ theme }) => ({
  paddingBottom: theme.spacing(1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-of-type': {
    borderBottom: 'none',
    paddingBottom: 0,
  },
}))

const MeetingLink = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'underline',
  display: 'inline-block',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
}))

const StyledDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(2, 3),
}))

const InternalEventLocationFields: React.FC<{ event: Event }> = ({ event }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const description = event?.description ?? ''
  const additionalInformation = event?.additionalInformation ?? ''
  const meetingLink = event?.onlineEventUrl ?? ''
  const eventLocationType = event?.isOnline ? 'Global' : 'Local'
  const registrationFormLink = event?.registrationLogisticsFormLink ?? ''
  const registrationStatus = event?.registrationOpen ? 'Open' : 'Closed'
  const earlyCloseDate = event?.registrationCloseDate
    ? dateToDashString(event.registrationCloseDate, IS_UTC)
    : ''
  const startDate = event?.startDate
    ? dateToDashString(event.startDate, IS_UTC)
    : ''
  const endDate = event?.endDate ? dateToDashString(event.endDate, IS_UTC) : ''
  const startTime = event?.startTime ?? ''
  const endTime = event?.endTime ?? ''
  const timeZone = event?.timezone ?? ''
  const hostName = event?.hostName ?? ''
  const hostPhone = event?.hostPhone ?? ''
  const contactName = event?.contactName ?? ''
  const contactEmail = event?.contactEmail ?? ''
  const address = event?.address

  return (
    <EventSection>
      <InfoSection
        header={
          <Header
            headerName={t('Events.Description.Header', 'Description')}
            component="h2"
            variant={HeaderVariant.Card}
            css={{ padding: '0' }}
          />
        }
        headerContainerProps={defaultHeaderContainerProps}
        sectionId="eventDescriptionFormHeader"
      >
        <Box sx={{ pt: 3, pb: 3 }}>
          <BannerBox sx={{ p: 2 }}>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.primary.dark }}
            >
              {description}
            </Typography>
          </BannerBox>
        </Box>
      </InfoSection>
      <StyledDivider />
      <InfoSection
        header={
          <Header
            headerName={t(
              'Events.AdditionalInformation.Header',
              'Additional Information'
            )}
            component="h2"
            variant={HeaderVariant.Card}
            css={{ padding: '0' }}
          />
        }
        headerContainerProps={defaultHeaderContainerProps}
        sectionId="additionalInformationFormHeader"
      >
        <Box sx={{ pt: 3, pb: 3 }}>
          <BannerBox sx={{ p: 2, minHeight: 80 }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.primary.dark,
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {additionalInformation}
            </Typography>
          </BannerBox>
        </Box>
        <Grid item xs={12}>
          <FieldRow>
            <LabelValue
              label={t(
                'Events.Registration.FormLink',
                'Registration Logistics Form Link'
              )}
              value={registrationFormLink}
            />
          </FieldRow>
        </Grid>
      </InfoSection>
      <StyledDivider />
      <InfoSection
        header={
          <Header
            headerName={t('Events.Location.Header', 'Location')}
            component="h2"
            variant={HeaderVariant.Card}
            css={{ padding: '0' }}
          />
        }
        headerContainerProps={defaultHeaderContainerProps}
        sectionId="eventLocationFormHeader"
      >
        <EventDetailsForm aria-labelledby="eventLocationFormHeader">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FieldRow>
                <LabelValue
                  label={t(
                    'Events.EventLocationField.FormField.Address',
                    'Street Address'
                  )}
                  value={address?.streetAddress1 ?? ''}
                />
              </FieldRow>
              <FieldRow>
                <LabelValue
                  label={t(
                    'Events.EventLocationField.FormField.Address2',
                    'Building, Suite, etc.'
                  )}
                  value={address?.streetAddress2 ?? ''}
                />
              </FieldRow>
              <FieldRow>
                <LabelValue
                  label={t('Events.EventLocationField.FormField.City', 'City')}
                  value={address?.city ?? ''}
                />
              </FieldRow>
            </Grid>

            <Grid item xs={12} md={6}>
              <FieldRow>
                <LabelValue
                  label={t('Events.EventForm.FormField.Country', 'Country')}
                  value={address?.countryCode ?? ''}
                />
              </FieldRow>
              <FieldRow>
                <LabelValue
                  label={t(
                    'Events.EventForm.FormField.State',
                    'State/Province'
                  )}
                  value={address?.state ?? ''}
                />
              </FieldRow>
              <FieldRow>
                <LabelValue
                  label={t(
                    'Events.EventLocationField.FormField.PostalCode',
                    'Postal Code'
                  )}
                  value={address?.zip ?? ''}
                />
              </FieldRow>
            </Grid>

            {eventLocationType === 'Global' && (
              <Grid item xs={12}>
                <FieldRow>
                  <LabelValue
                    label={t(
                      'Events.EventLocationField.FormField.MeetingLink',
                      'Meeting Link'
                    )}
                    value={
                      meetingLink ? (
                        <MeetingLink
                          href={meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {meetingLink}
                        </MeetingLink>
                      ) : (
                        ''
                      )
                    }
                  />
                </FieldRow>
              </Grid>
            )}

            <Grid item xs={12}>
              <StyledDivider />
            </Grid>
          </Grid>
        </EventDetailsForm>
      </InfoSection>

      <InfoSection
        header={
          <Header
            headerName={t('Event.DateAndTime', 'Date and Time')}
            component="h2"
            variant={HeaderVariant.Card}
            css={{ padding: '0' }}
          />
        }
        headerContainerProps={defaultHeaderContainerProps}
        sectionId="eventLocationFormHeader"
      >
        <EventDetailsForm aria-labelledby="eventLocationFormHeader">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FieldRow>
                    <LabelValue
                      label={t(
                        'Events.EventLocationField.FormField.StartDate',
                        'Start Date'
                      )}
                      value={
                        <span
                          data-testid={
                            INTERNAL_EVENT_LOCATION_TEST_IDS.locationStartDateValue
                          }
                        >
                          {startDate}
                        </span>
                      }
                    />
                  </FieldRow>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FieldRow>
                    <LabelValue
                      label={t(
                        'Events.EventLocationField.FormField.StartTime',
                        'Start Time'
                      )}
                      value={startTime}
                    />
                  </FieldRow>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FieldRow>
                    <LabelValue
                      label={t(
                        'Events.EventLocationField.FormField.TimeZone',
                        'Time Zone'
                      )}
                      value={timeZone}
                    />
                  </FieldRow>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FieldRow>
                    <LabelValue
                      label={t(
                        'Events.EventLocationField.FormField.EndDate',
                        'End Date'
                      )}
                      value={
                        <span
                          data-testid={
                            INTERNAL_EVENT_LOCATION_TEST_IDS.locationEndDateValue
                          }
                        >
                          {endDate}
                        </span>
                      }
                    />
                  </FieldRow>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FieldRow>
                    <LabelValue
                      label={t(
                        'Events.EventLocationField.FormField.EndTime',
                        'End Time'
                      )}
                      value={endTime}
                    />
                  </FieldRow>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </EventDetailsForm>
      </InfoSection>
      <StyledDivider />
      <InfoSection
        header={
          <Header
            headerName={t('Events.Registration.Header', 'Registration')}
            component="h2"
            variant={HeaderVariant.Card}
            css={{ padding: '0' }}
          />
        }
        headerContainerProps={defaultHeaderContainerProps}
        sectionId="registrationSection"
      >
        <Box sx={{ pt: 3, pb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FieldRow>
                <LabelValue
                  label={t('Events.Registration.FormField.Status', 'Status')}
                  value={registrationStatus}
                />
              </FieldRow>
            </Grid>
            <Grid item xs={12} md={6}>
              <FieldRow>
                <LabelValue
                  label={t(
                    'Events.Registration.FormField.EarlyCloseDate',
                    'Early Close Date'
                  )}
                  value={
                    <span
                      data-testid={
                        INTERNAL_EVENT_LOCATION_TEST_IDS.registrationEarlyCloseDateValue
                      }
                    >
                      {earlyCloseDate}
                    </span>
                  }
                />
              </FieldRow>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={<Checkbox checked={!!earlyCloseDate} disabled />}
                  label={t(
                    'Events.Registration.ClosedEarly',
                    'Registration Closed Early'
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </InfoSection>
      <StyledDivider />
      <InfoSection
        header={
          <Header
            headerName={t(
              'Events.HostContact.Header',
              'Host and Contact Information'
            )}
            component="h2"
            variant={HeaderVariant.Card}
            css={{ padding: '0' }}
          />
        }
        headerContainerProps={defaultHeaderContainerProps}
        sectionId="hostContactSection"
      >
        <Box sx={{ pt: 3, pb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FieldRow>
                <LabelValue
                  label={t(
                    'Events.EventLocationField.FormField.HostName',
                    'Host Name'
                  )}
                  value={hostName}
                />
              </FieldRow>
            </Grid>
            <Grid item xs={12} md={6}>
              <FieldRow>
                <LabelValue
                  label={t(
                    'Events.EventLocationField.FormField.ContactName',
                    'Contact Name'
                  )}
                  value={contactName}
                />
              </FieldRow>
            </Grid>
            <Grid item xs={12} md={6}>
              <FieldRow>
                <LabelValue
                  label={t(
                    'Events.EventLocationField.FormField.HostPhone',
                    'Host Phone'
                  )}
                  value={hostPhone}
                />
              </FieldRow>
            </Grid>
            <Grid item xs={12} md={6}>
              <FieldRow>
                <LabelValue
                  label={t(
                    'Events.EventLocationField.FormField.ContactEmail',
                    'Contact Email'
                  )}
                  value={contactEmail}
                />
              </FieldRow>
            </Grid>
          </Grid>
        </Box>
      </InfoSection>
    </EventSection>
  )
}

export default InternalEventLocationFields
